import React, { CSSProperties } from 'react'

import StepIndicators from '@/components/flows/WalkthroughFlow/components/StepIndicators'
import { useWalkthroughSteps } from '@/components/flows/WalkthroughFlow/hooks/useWalkthroughSteps'
import { WalkthroughFlowProps } from '@/components/flows/WalkthroughFlow/WalkthroughFlow'
import ActionButton from '@/components/misc/ActionButton'
import { Button } from '@/components/ui/button'
import { Container } from '@/components/ui/container'
import { Heading } from '@/components/ui/heading'

type Props = Pick<
  WalkthroughFlowProps,
  'title' | 'variant' | 'completeButtonText' | 'onComplete' | 'isCompleting'
>

const StepControls: React.FC<Props> = ({
  title,
  variant,
  completeButtonText,
  onComplete,
  isCompleting = false,
}) => {
  const {
    canGoToNextStep,
    canGoToPreviousStep,
    goToNextStep,
    goToPreviousStep,
  } = useWalkthroughSteps()

  const isPageVariant = variant === 'page'

  const styles: CSSProperties = {
    minWidth: '100px',
  }

  function renderBackButton() {
    if (!canGoToPreviousStep) {
      return null
    }

    return (
      <Button
        variant="outline-secondary"
        size="lg"
        style={styles}
        onClick={goToPreviousStep}
      >
        Back
      </Button>
    )
  }

  function renderNextButton() {
    return canGoToNextStep ? (
      <Button variant="primary" size="lg" style={styles} onClick={goToNextStep}>
        Next
      </Button>
    ) : (
      <ActionButton
        isProcessing={isCompleting}
        style={{ minWidth: '100px' }}
        variant="primary"
        size="lg"
        onClick={onComplete}
      >
        {completeButtonText}
      </ActionButton>
    )
  }

  function renderControls() {
    return (
      <Container
        className="flex flex-col-reverse items-center justify-end gap-4
          sm:flex-row"
      >
        <StepIndicators />
        {renderBackButton()}
        {renderNextButton()}
      </Container>
    )
  }

  function renderAsPage() {
    return (
      <header
        className="box-shadow-lg fixed top-0 right-0 left-0 w-full border
          bg-white p-4"
      >
        {renderControls()}
      </header>
    )
  }

  function renderAsComponent() {
    return (
      <Container
        className="flex flex-col items-center justify-between gap-4 sm:flex-row"
      >
        <Heading size={2} className="text-xxl m-0 shrink-0 text-center">
          {title}
        </Heading>
        {renderControls()}
      </Container>
    )
  }

  return isPageVariant ? renderAsPage() : renderAsComponent()
}

export default StepControls
